import * as React from "react";
import { createPortal } from "react-dom";

import { withDefault } from "collection-utils";

import { Wrapper, CommentButton } from "./chat-style";
import type { CommentData } from "../comments/comments";
import { nonNull } from "common/Support";
import type { BubbleStyle } from "./private/chat-bubble";
import ChatBubble from "./private/chat-bubble";
import FloatingButton from "../../views/floating-button/floating-button";
import type { HasOverlayState, HasOverlayProps } from "../has-overlay-component/has-overlay-component";
import HasOverlayComponent from "../has-overlay-component/has-overlay-component";
import OverlayEditor from "../../deprecated/OverlayEditor";
import { APP_MODAL_ROOT } from "../../../lib/constants";
import { getLocalizedString } from "common/components/localization";
import type { AppUtilities } from "../../../lib/app-utility-context";
import { AppUtilitiesConsumer } from "../../../lib/app-utility-context";
import type { RuntimeTheme } from "../../../lib/theme/theme";
import { withTheme } from "styled-components";
import ConfirmModal from "../../../chrome/views/confirm-modal/confirm-modal";
import type { AppEnvironment, BasicUserProfile } from "common/components/types";
import { AuthScreenMode } from "../../../chrome/smart/app-modal-provider/app-modal-provider-types";
import type { AppCredentials } from "../../../chrome/smart/app-container/app-container-types";
import { requestPushNotificationPermissionIfNecessary } from "../../../lib/push-notifications";
import { withCredentials } from "../../../lib/with-credentials";
import type { ModalController} from "../../../chrome/smart/app-modal-provider/app-modal-provider";
import { withModals } from "../../../chrome/smart/app-modal-provider/app-modal-provider";
import { Trash, Plus } from "common/react-plucked";
import EmptyWrapper from "../../views/empty-wrapper/empty-wrapper";
import { getScrollParent } from "../list/list";
import { ConfirmModalStyle } from "common/components/confirm-modal";
import type { AppKind } from "common/components/app-kind";

// FIXME: Merge this with the same props in `Comments.tsx`?
interface BaseProps extends HasOverlayProps {
    readonly comments: ReadonlyArray<CommentData>;
    readonly postComment: ((username: string, email: string | undefined, comment: string) => void) | undefined;
    readonly onTapNew: (() => void) | undefined;
    readonly deleteComments?: (documentIDs: ReadonlyArray<string>) => void;
    readonly appEnvironment: AppEnvironment;
    readonly topic: string | undefined;
    readonly userProfiles: readonly BasicUserProfile[] | undefined;
}

interface ThemeProps {
    theme: RuntimeTheme;
}

interface Props extends BaseProps {
    readonly credentials: AppCredentials;
    readonly modals: ModalController;
    readonly appUtilities: AppUtilities;
}

interface State extends HasOverlayState {
    selection: Set<string> | undefined;
    showDeleteConfirm: boolean;
}

class ChatInner extends HasOverlayComponent<Props & ThemeProps, State> {
    public state: State = {
        selection: undefined,
        showDeleteConfirm: false,
    };

    private get appKind(): AppKind {
        return this.props.appEnvironment.appKind;
    }

    private bottomRef = React.createRef<HTMLDivElement>();

    private onEditClick = async (): Promise<void> => {
        const { credentials, onTapNew } = this.props;
        const { isAuthenticated, username, email } = credentials;
        const loggedIn = isAuthenticated && username.length > 0 && email.length > 0;
        if (!loggedIn) {
            const r = await this.props.modals.openAuthModal(
                getLocalizedString("chat", this.appKind),
                AuthScreenMode.Username
            );
            if (!r) return;
            await requestPushNotificationPermissionIfNecessary();
        }
        if (onTapNew !== undefined) {
            onTapNew();
        } else {
            this.showOverlay();
        }
    };

    private onEditorClosed = () => {
        this.hideOverlay();
    };

    private onDeleteClick = (): void => {
        this.setState({ showDeleteConfirm: true });
    };

    private onEditorSaved = (text: string) => {
        if (this.props.postComment === undefined) {
            return;
        }
        this.hideOverlay();
        this.props.postComment(this.props.credentials.username, this.props.credentials.email, text);
    };

    private isFromSameAuthor = (c1: CommentData, c2: CommentData): boolean =>
        c1.email === c2.email && c1.username === c2.username;
    private commentHasSameAuthorAsNext = (i: number): boolean => {
        const { comments } = this.props;
        if (i + 1 === comments.length) {
            return false;
        }

        return this.isFromSameAuthor(comments[i], comments[i + 1]);
    };

    public async componentDidMount() {
        const { topic, appEnvironment } = this.props;
        if (topic !== undefined) {
            appEnvironment.suppressNotificationTopic(topic);
        }
        this.scrollToEnd();
    }

    public componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.comments.length !== prevProps.comments.length) {
            this.scrollToEnd();
        }

        if (this.state.selection !== prevState.selection) {
            this.updateAppBarOverlay();
        }
    }

    private scrollToEnd = () => {
        const bottom = this.bottomRef.current;

        const scroller = getScrollParent(bottom);

        if (scroller !== undefined) {
            scroller.scrollTop = 10000000;
        }
    };

    // We are currently assuming that Chat topics cannot change throughout
    // the lifecycle of this component.
    public componentWillUnmount() {
        const { topic, appEnvironment } = this.props;
        if (topic !== undefined) {
            appEnvironment.unsuppressNotificationTopic(topic);
        }
    }

    private token: any = undefined;

    private onLongPress = (_documentID: string): void => {
        if (this.props.deleteComments === undefined) return;
        if (this.state.selection === undefined) {
            this.setState({ selection: new Set<string>() });
        }
    };

    private getMessagesString(): string | undefined {
        if (this.state.selection === undefined) return undefined;

        if (this.state.selection.size === 1) {
            return "1 message";
        } else {
            return `${this.state.selection.size} messages`;
        }
    }

    private updateAppBarOverlay() {
        if (this.state.selection === undefined) return;

        const title: string = `${this.getMessagesString()} selected`;
        this.token = this.props.appUtilities.overlayAppBar(
            {
                title,
                barButtonItems: [
                    {
                        title: "Cancel",
                        style: "text",
                        icon: "00-01-glide-close",
                        onClick: this.onCancel,
                    },
                ],
            },
            this.token
        );
    }

    private onCancel = (): void => {
        this.setState({ selection: undefined });
        this.props.appUtilities.removeAppBarOverlay(this.token);
    };

    private onSelected = (documentID: string): void => {
        // Do nothing
        let selection = this.state.selection;
        if (selection === undefined) return;
        selection = new Set(selection);

        if (selection.has(documentID)) {
            selection.delete(documentID);
        } else {
            selection.add(documentID);
        }

        this.setState({ selection });
    };

    private onConfirmFinish = (accepted: boolean) => {
        if (accepted && this.props.deleteComments !== undefined && this.state.selection !== undefined) {
            const toDelete = Array.from(this.state.selection);
            this.props.deleteComments(toDelete);
            this.setState({ selection: undefined, showDeleteConfirm: false });
            this.props.appUtilities.removeAppBarOverlay(this.token);
        } else {
            this.setState({ showDeleteConfirm: false });
        }
    };

    public render(): React.ReactNode {
        const { appKind } = this;
        let portal: React.ReactNode;
        let commentButton: React.ReactNode;
        if (this.shouldRenderPortal()) {
            let content: React.ReactNode;
            const creds = this.props.credentials;
            if (creds.isAuthenticated && creds.email.length > 0 && creds.username.length > 0) {
                const { renderEnvironment } = this.props;
                content = (
                    <OverlayEditor
                        renderEnvironment={renderEnvironment}
                        title={getLocalizedString("newMessage", appKind)}
                        placeholder={getLocalizedString("writeMessage", appKind)}
                        saveText={getLocalizedString("send", appKind)}
                        saveIcon={"send"}
                        saved={this.props.postComment !== undefined ? this.onEditorSaved : undefined}
                        closed={this.onEditorClosed}
                        allowEmpty={false}
                    />
                );
                portal = this.getPortal(content);
            }
        } else {
            const AddIcon = this.state.selection !== undefined ? Trash : Plus;
            const enabled = this.state.selection === undefined || this.state.selection.size > 0;
            const color = this.state.selection === undefined ? undefined : this.props.theme.destroyColor;
            const onClick = this.state.selection === undefined ? this.onEditClick : this.onDeleteClick;
            commentButton = (
                <CommentButton data-test="app-new-chat-wrap">
                    <FloatingButton color={color} isEnabled={enabled} onClick={onClick}>
                        <AddIcon size={24} color={this.props.theme.accentForegroundColor} />
                    </FloatingButton>
                </CommentButton>
            );
        }

        let confirm: React.ReactNode;
        if (this.state.showDeleteConfirm) {
            confirm = createPortal(
                <ConfirmModal
                    title={`Are you sure you want to delete ${this.getMessagesString()}?`}
                    description="This action cannot be undone"
                    accept="Delete"
                    cancel="Cancel"
                    modalStyle={ConfirmModalStyle.Delete}
                    onFinish={this.onConfirmFinish}
                />,
                nonNull(document.getElementById(APP_MODAL_ROOT))
            );
        }

        const comments = this.props.comments;

        if (comments.length === 0) {
            return (
                <>
                    <EmptyWrapper forceDisplay={true}>
                        {portal}
                        {confirm}
                        {getLocalizedString("noMessages", appKind)}
                        <div ref={this.bottomRef} />
                    </EmptyWrapper>
                    {commentButton}
                </>
            );
        }
        return (
            <>
                <Wrapper>
                    {portal}
                    {confirm}
                    {comments.map((c, i) => {
                        let style: BubbleStyle = "normal";
                        const sameAuthor = this.commentHasSameAuthorAsNext(i);
                        if (this.state.selection !== undefined) {
                            if (this.state.selection.has(c.documentID)) {
                                style = "selected";
                            } else {
                                style = "unselected";
                            }
                        }

                        let username = c.username;
                        let image: string | undefined;
                        const profiles = this.props.userProfiles;
                        if (profiles !== undefined) {
                            const profile = profiles.find(p => p.email === c.email);
                            if (profile !== undefined) {
                                username = profile.name ?? username;
                                image = profile.image;
                            }
                        }

                        return (
                            <ChatBubble
                                token={c.documentID}
                                onLongPress={this.onLongPress}
                                onSelected={this.onSelected}
                                bubbleStyle={style}
                                key={i}
                                orientation={c.email === this.props.credentials.email ? "right" : "left"}
                                text={c.comment}
                                userEmail={withDefault(c.email, "")}
                                image={image}
                                userName={username}
                                useAccent={true}
                                isFromSameAuthor={sameAuthor}
                            />
                        );
                    })}
                    <div ref={this.bottomRef} />
                </Wrapper>
                {commentButton}
            </>
        );
    }
}

const ThemedInner = withModals(withCredentials(withTheme(ChatInner)));

export default function Chat(props: BaseProps) {
    return <AppUtilitiesConsumer>{utils => <ThemedInner {...props} appUtilities={utils} />}</AppUtilitiesConsumer>;
}
